/**
 * The look every React Flow canvas shares — Architecture, the Git branch map,
 * the team map and a plan's graph — as plain data, so it can be tested
 * without a DOM and the canvases cannot drift apart. The components that
 * wear it (the frame, the band node, the count pills) are in flowCanvas.tsx;
 * the classes they use are the "Flow canvases" block of index.css.
 *
 *   flowEdge   an orthogonal (`smoothstep`) edge with a closed arrow, muted
 *              unless told otherwise, its label on a small surface chip; one
 *              that would otherwise run behind a card says which way round to
 *              go (`via`) and is drawn by FlowViaEdge along flowViaPath
 *   bandAround the rectangle a group band needs to hold its cards, with room
 *              for its title and description above them
 *   flowBand   that band as a node: behind everything, not selectable, and
 *              transparent to the pointer so the canvas still pans across it
 */
import type { Edge, Node } from '@xyflow/react';
import type { ReactNode } from 'react';

/** ArtTone (ui/Art.tsx), spelled out: this file stays free of JSX so the Node tests can load it. */
export type ArtTone = 'teal' | 'violet' | 'amber' | 'blue' | 'rose' | 'green' | 'slate';

/** A tone as a colour, for an edge stroke or a legend swatch (see `--tone-*` in index.css). */
export const FLOW_TONE: Record<ArtTone, string> = {
  teal: 'var(--tone-teal)',
  violet: 'var(--tone-violet)',
  amber: 'var(--tone-amber)',
  blue: 'var(--tone-blue)',
  rose: 'var(--tone-rose)',
  green: 'var(--tone-green)',
  slate: 'var(--tone-slate)',
};

/** An edge nobody is looking at, and one related to what is selected. */
export const FLOW_MUTED = 'var(--color-muted)';
export const FLOW_ACCENT = 'var(--color-accent)';

/** The class for every `<Handle>`: small, muted, no border. */
export const FLOW_HANDLE = 'flow-handle';

/**
 * Band geometry: padding round the cards, and the head that holds the title
 * and a description of up to two lines (12px top, a 19px title, two 16px
 * lines, a little air above the first card).
 */
export const BAND_PAD = 16;
export const BAND_HEAD = 68;

export interface FlowEdgeLook {
  /** Stroke colour; muted when left out. The arrow takes the same colour. */
  stroke?: string;
  width?: number;
  opacity?: number;
  dashed?: boolean;
  animated?: boolean;
  label?: string;
  zIndex?: number;
  /**
   * The arrowhead's size on screen, in pixels. React Flow sizes a marker in
   * stroke widths, so left alone a 2.5px line carries an arrow two and a half
   * times the size of a hairline's; a canvas whose thick lines mean "important"
   * rather than "many" asks for one size. Left out, the arrow grows with the line.
   */
  arrow?: number;
  /**
   * The corners to go by, for an edge a layout has found a clear way round
   * for. Left to itself an orthogonal edge turns half way between its ends,
   * which is wherever a card happens to be: it runs behind the card and comes
   * out looking like two edges that card has. See flowViaPath.
   */
  via?: readonly FlowPoint[];
}

export interface FlowPoint {
  x: number;
  y: number;
}

/** The edge type for an edge with `via` corners (FlowViaEdge in flowCanvas.tsx, which every FlowCanvas knows). */
export const FLOW_VIA_EDGE = 'flowVia';

/**
 * An orthogonal path from `source` to `target` by way of `via`, its corners
 * rounded like a smoothstep edge's. It leaves the source and arrives at the
 * target horizontally (handles are on the left and right of a card), so the
 * first and last corners are added here: level with the source above the
 * first `via`, and level with the target above the last.
 */
export function flowViaPath(source: FlowPoint, target: FlowPoint, via: readonly FlowPoint[], radius = 6): string {
  if (via.length === 0) return `M ${source.x} ${source.y} L ${target.x} ${target.y}`;
  const corners = [source, { x: via[0].x, y: source.y }, ...via, { x: via[via.length - 1].x, y: target.y }, target]
    // A corner on top of the one before it (a via already level with the source) is no corner,
    .filter((p, i, all) => i === 0 || p.x !== all[i - 1].x || p.y !== all[i - 1].y)
    // and neither is a point on a straight run.
    .filter((p, i, all) => i === 0 || i === all.length - 1 || !((all[i - 1].x === p.x && p.x === all[i + 1].x) || (all[i - 1].y === p.y && p.y === all[i + 1].y)));
  let d = `M ${corners[0].x} ${corners[0].y}`;
  for (let i = 1; i < corners.length - 1; i++) {
    const [a, p, b] = [corners[i - 1], corners[i], corners[i + 1]];
    const inLen = Math.hypot(p.x - a.x, p.y - a.y);
    const outLen = Math.hypot(b.x - p.x, b.y - p.y);
    const r = Math.min(radius, inLen / 2, outLen / 2);
    if (r <= 0) continue;
    const before = { x: p.x - ((p.x - a.x) / inLen) * r, y: p.y - ((p.y - a.y) / inLen) * r };
    const after = { x: p.x + ((b.x - p.x) / outLen) * r, y: p.y + ((b.y - p.y) / outLen) * r };
    d += ` L ${before.x} ${before.y} Q ${p.x} ${p.y} ${after.x} ${after.y}`;
  }
  const end = corners[corners.length - 1];
  return `${d} L ${end.x} ${end.y}`;
}

export function flowEdge(ends: { id: string; source: string; target: string; sourceHandle?: string; targetHandle?: string }, look: FlowEdgeLook = {}): Edge {
  const stroke = look.stroke ?? FLOW_MUTED;
  const head = look.arrow === undefined ? 14 : Math.round((look.arrow / Math.max(1, look.width ?? 1)) * 10) / 10;
  return {
    ...ends,
    type: look.via?.length ? FLOW_VIA_EDGE : 'smoothstep',
    ...(look.via?.length ? { data: { via: look.via } } : {}),
    animated: look.animated ?? false,
    label: look.label,
    labelStyle: { fontSize: 10, fill: 'var(--color-foreground)' },
    labelBgStyle: { fill: 'var(--color-surface)', fillOpacity: 0.95 },
    labelBgPadding: [4, 2],
    labelBgBorderRadius: 4,
    markerEnd: { type: 'arrowclosed', width: head, height: head, color: stroke },
    style: { stroke, strokeWidth: look.width ?? 1, opacity: look.opacity ?? 0.6, ...(look.dashed ? { strokeDasharray: '4 4' } : {}) },
    ...(look.zIndex !== undefined ? { zIndex: look.zIndex } : {}),
  };
}

export interface FlowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The band that holds `members`, or undefined for an empty group (which draws no band). */
export function bandAround(members: readonly FlowRect[], pad = BAND_PAD, head = BAND_HEAD): FlowRect | undefined {
  if (members.length === 0) return undefined;
  const left = Math.min(...members.map((m) => m.x));
  const top = Math.min(...members.map((m) => m.y));
  const right = Math.max(...members.map((m) => m.x + m.width));
  const bottom = Math.max(...members.map((m) => m.y + m.height));
  return { x: left - pad, y: top - head, width: right - left + pad * 2, height: bottom - top + head + pad };
}

export interface FlowBandData extends Record<string, unknown> {
  title: string;
  description?: string;
  tone?: ArtTone;
  /** A running plan's band (D178): drawn in the plan's colour slot rather than a tone. */
  planTone?: number;
  /** A small icon in place of the dot: the Office's place groups (D336) show what kind of place it is. */
  icon?: ReactNode;
}

/**
 * Past this many cards a canvas gets its minimap. Below it the whole graph
 * already fits on screen, and an overview of it only repeats the canvas in
 * miniature — and on a short canvas (the Git view's) sits on top of a card.
 */
export const MINIMAP_MIN_CARDS = 8;

/** A canvas can overrule this (FlowCanvas `minimap`): a plan wants one for three waves, however few cards are on them. */
export function showMinimap(nodes: ReadonlyArray<{ type?: string }> | undefined): boolean {
  return (nodes ?? []).filter((n) => n.type !== 'band').length > MINIMAP_MIN_CARDS;
}

/**
 * A canvas whose graph is fitted to its frame has nowhere spare for the zoom
 * buttons and the overview: left in their corners they sit on the first and
 * last cards. Such a canvas keeps a strip clear along the bottom instead
 * (FlowCanvas `footer`), and adds `flowFooter()` to the bottom of its
 * fit-view padding: a row of buttons, or the overview when there is one.
 */
export const FLOW_FOOTER = { controls: 44, minimap: { width: 168, height: 96 } } as const;

export function flowFooter(minimap: boolean): number {
  // React Flow stands a panel 15px off the frame's edge.
  return minimap ? FLOW_FOOTER.minimap.height + 22 : FLOW_FOOTER.controls;
}

export function flowBand(id: string, rect: FlowRect, data: FlowBandData): Node<FlowBandData> {
  return {
    id,
    type: 'band',
    position: { x: rect.x, y: rect.y },
    data,
    // Transparent to the pointer: dragging across a band pans the canvas and
    // a click on it lands on the pane, as on the dotted ground around it.
    style: { width: rect.width, height: rect.height, pointerEvents: 'none' },
    selectable: false,
    draggable: false,
    focusable: false,
    zIndex: -1,
  };
}
